import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { assertPagePermission } from "@/features/auth/lib/assert-page-permission";
import { pageStateQueryOptions, personasQueryOptions, PageAccessError } from "@/lib/queries";
import { PageError, PageAccessErrorComponent } from "@/features/auth/components/PageError";
import { usePageState } from "@/hooks/usePageState";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { PersonasPage } from "@/features/personas/components/PersonasPage/PersonasPage";
import { PersonasLoadingState } from "@/features/personas/components/PersonasLoadingState/PersonasLoadingState";

function PersonasRoute() {
  const { t } = useTranslation();
  const { persona } = Route.useSearch();
  useDocumentTitle(t("page.title.personas"));
  const { isLoading, error } = usePageState("personas");
  if (isLoading) return <PersonasLoadingState />;
  if (error) return <PageError is403={error instanceof PageAccessError} />;
  return <PersonasPage initialSelectedPersonaId={persona} />;
}

export const Route = createFileRoute("/_authenticated/personas")({
  validateSearch: (search: Record<string, unknown>) => ({
    persona: typeof search.persona === "string" ? search.persona : undefined,
  }),
  beforeLoad: () => assertPagePermission("personas"),
  loader: ({ context }) => {
    const tenantId = useAuth.getState().activeTenantId;
    if (tenantId) {
      void context.queryClient.prefetchQuery(personasQueryOptions(tenantId));
    }
    return context.queryClient
      .ensureQueryData(pageStateQueryOptions("personas"))
      .catch((err: unknown) => {
        if (err instanceof PageAccessError) return null;
        throw err;
      });
  },
  component: PersonasRoute,
  errorComponent: PageAccessErrorComponent,
});
